import { useCallback, useState } from "react";

import Icon from "~/components/shared/Icon";

import Steps from "~/components/build/Header/pipeline/Steps";
import { Build, Job } from "~/components/build/Show/lib/types";

function getHiddenJobsCount(jobs?: Array<Job>) {
  if (!jobs) {
    return 0;
  }

  return jobs.filter(
    (job) =>
      (job.type !== "waiter" && job.state === "broken") ||
      ((job.type === "script" || job.type === "trigger") && job.retriedInJobUuid),
  ).length;
}

export default function BuildHeader({
  build,
  setBuildData,
  reloadBuild,
}: {
  build: {
    id: string;
    path: string;
    jobs?: Array<Job>;
    jobsCount: number;
  };
  setBuildData: (build: Build) => void;
  reloadBuild: () => void;
}) {
  const hiddenJobsCount = getHiddenJobsCount(build.jobs);

  const [showHiddenJobs, setShowHiddenJobs] = useState(false);

  const toggleHiddenJobs = useCallback(
    (event) => {
      event.preventDefault();

      if (event.target instanceof HTMLElement) {
        event.target.blur();
      }

      setShowHiddenJobs(!showHiddenJobs);
    },
    [showHiddenJobs],
  );

  return (
    <div className="text-charcoal-900 flex">
      <div className="gap1 mx-4 my-2 flex min-w-0 flex-auto flex-wrap">
        <Steps
          build={build}
          setBuildData={setBuildData}
          reloadBuild={reloadBuild}
          showHiddenJobs={showHiddenJobs}
        />
      </div>

      <div className="flex-none" style={{ paddingTop: 18 }}>
        {Boolean(hiddenJobsCount) && (
          <button
            className="btn dark-gray hover-black right-tooltip ml-0 mr-2 mt-0 pl-0 pt-0 font-normal"
            onClick={toggleHiddenJobs}
            aria-label={`${showHiddenJobs ? "Hide" : "Show"} ${hiddenJobsCount} hidden ${
              hiddenJobsCount > 1 ? "jobs" : "job"
            }`}
            data-title-tooltip={`${showHiddenJobs ? "Hide" : "Show"} ${hiddenJobsCount} hidden ${
              hiddenJobsCount > 1 ? "jobs" : "job"
            }`}
            style={{ width: 22, height: 22 }}
          >
            <Icon
              icon={showHiddenJobs ? "eye-strikethrough" : "eye"}
              className="relative"
              style={{ top: -3 }}
            />
          </button>
        )}
      </div>
    </div>
  );
}

// We need to be able to conditionally wrap content for styling purposes, in particular
// for hard failed steps which have different layout requirements to 'regular' steps.
// Some day I would love to tease apart the abstractions for step pills into more
// concrete implementations, but this will get us by for now.
export const StepContentWrapper = ({
  children = null,
  shouldWrap = true,
}: {
  children?: React.ReactNode;
  shouldWrap?: boolean;
}) => {
  return shouldWrap ? <div className="build-pipeline-job-content">{children}</div> : children;
};
